import { Component, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, Subject, Subscription } from 'rxjs';
import { debounceTime, take } from 'rxjs/operators';

import {
  AggregationDataOnlyDocument,
  DataOnlyDocument,
} from '../../../generated/graphql.generated';
import { PlusApollo } from '../../core/auth/graphql.module';
import { ConfigService } from '../../core/config/config.service';
import { GraphQLModule } from '../../core/public-api';
import { currentTimeSeconds } from '../../time';
import { DashboardStore } from '../dashboardv2.store';

interface BillingRow {
  zoneName: string;
  zoneId?: string;
  franchiseId?: string;
  franchiseName?: string;
  activationCount: number;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  totalCost: number;
  costPerActivation: number;
  jobCount: number;
  activationsPerJob: number;
  costPerJob: number;
  askBeeCount: number;
  outboundCallCount: number;
  inboundCallCount: number;
  scheduleCommCount: number;
  updateInventoryCount: number;
  updateInventoryCost: number;
  updateInventoryTokens: number;
  monthlyRate: number;
  billingCurrency: string;
  includedActivations: number;
  overageCount: number;
  overageCost: number;
  totalCostBilling: number;
  overageRate: number;
  saving: boolean;
}

const ZERO_ROW: Omit<BillingRow, 'zoneName' | 'overageRate' | 'saving'> = {
  activationCount: 0,
  promptTokens: 0,
  completionTokens: 0,
  totalTokens: 0,
  totalCost: 0,
  costPerActivation: 0,
  jobCount: 0,
  activationsPerJob: 0,
  costPerJob: 0,
  askBeeCount: 0,
  outboundCallCount: 0,
  inboundCallCount: 0,
  scheduleCommCount: 0,
  updateInventoryCount: 0,
  updateInventoryCost: 0,
  updateInventoryTokens: 0,
  monthlyRate: 0,
  billingCurrency: 'USD',
  includedActivations: 0,
  overageCount: 0,
  overageCost: 0,
  totalCostBilling: 0,
};

const BILLING_DATA_COLUMNS = [
  'franchise',
  'activationCount',
  'activationPromptTokens',
  'activationCompletionTokens',
  'activationTotalTokens',
  'activationTotalCost',
  'activationCostPerActivation',
  'activationJobCount',
  'activationActivationsPerJob',
  'activationCostPerJob',
  'activationAskBeeCount',
  'activationOutboundCallCount',
  'activationInboundCallCount',
  'activationScheduleCommCount',
  'activationUpdateInventoryCount',
  'activationUpdateInventoryCost',
  'activationUpdateInventoryTokens',
  'billingMonthlyRate',
  'billingCurrency',
  'billingIncludedActivations',
  'billingOverageCount',
  'billingOverageCost',
  'billingTotalCost',
  'billingFranchiseId',
];

@Component({
  selector: 'app-dashboard-billing',
  templateUrl: './dashboard-billing.component.html',
  styleUrls: [
    '../dashboardv2.component.scss',
    './dashboard-billing.component.scss',
  ],
})
export class DashboardBillingComponent implements OnInit, OnDestroy {

  tableData: BillingRow[] = [];
  loading = true;
  lastReload: number | null = null;
  totals: BillingRow = {
    ...ZERO_ROW, zoneName: 'Total', overageRate: 0.25, saving: false,
  };

  private rowSave$ = new Map<string, Subject<BillingRow>>();
  private subs: Subscription[] = [];

  constructor(
    private dashboardStore: DashboardStore,
    private graphqlModule: GraphQLModule,
    private configService: ConfigService,
    private apollo: PlusApollo,
  ) {}

  triggerReload(): void {
    this.dashboardStore.triggerReload();
  }

  ngOnInit(): void {
    this.subs.push(
      combineLatest([
        this.dashboardStore.unixPeriod$,
        this.dashboardStore.reloadKey$,
      ]).subscribe(() => {
        this.loadData();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
    this.rowSave$.forEach((s) => s.complete());
  }

  onMonthlyRateChange(row: BillingRow, value: string): void {
    row.monthlyRate = parseFloat(value) || 0;
    this.scheduleRowSave(row);
  }

  onIncludedActivationsChange(row: BillingRow, value: string): void {
    row.includedActivations = parseInt(value, 10) || 0;
    this.scheduleRowSave(row);
  }

  onOverageRateChange(row: BillingRow, value: string): void {
    row.overageRate = parseFloat(value) || 0;
    this.scheduleRowSave(row);
  }

  private scheduleRowSave(row: BillingRow): void {
    const zoneId = row.zoneId || row.franchiseId;
    if (!zoneId) return;

    if (!this.rowSave$.has(zoneId)) {
      const subject = new Subject<BillingRow>();
      this.rowSave$.set(zoneId, subject);
      this.subs.push(
        subject.pipe(debounceTime(800)).subscribe((r) => this.saveRow(r)),
      );
    }

    this.rowSave$.get(zoneId)!.next(row);
  }

  private saveRow(row: BillingRow): void {
    const zoneId = row.zoneId || row.franchiseId;
    if (!zoneId) return;
    row.saving = true;

    this.configService.setConfigValues({
      configs: [
        { key: 'billing.monthlyRate', value: String(row.monthlyRate) },
        { key: 'billing.includedActivations', value: String(row.includedActivations) },
        { key: 'billing.overageRate', value: String(row.overageRate) },
      ],
    }, zoneId).subscribe({
      next: () => { row.saving = false; },
      error: () => { row.saving = false; },
    });
  }

  private loadData(): void {
    const zone = this.graphqlModule.zone.value;

    if (!zone) {
      return;
    }

    this.loading = true;

    this.dashboardStore.unixPeriod$.pipe(take(1)).subscribe(({ start, end }) => {
      this.subs.push(
        this.apollo.query({
          query: DataOnlyDocument,
          variables: {
            start,
            end,
            periodType: 'activationCreatedAt' as any,
            groupBy: 'activation' as any,
            dataColumns: BILLING_DATA_COLUMNS,
            sort: null as any,
            performance: 'skip-count' as any,
            skip: 0,
            limit: -1,
            zone,
          },
        }).subscribe(({ data }: any) => {
          const rows = data?.previewReport?.data?.rows ?? [];
          const headers = data?.previewReport?.data?.headers ?? [];
          this.tableData = this.parseRows(headers, rows);
          this.loading = false;
          this.lastReload = currentTimeSeconds();
          this.dashboardStore.setLastReload(this.lastReload);
        }),
      );

      this.subs.push(
        this.apollo.query({
          query: AggregationDataOnlyDocument,
          variables: {
            start,
            end,
            periodType: 'activationCreatedAt' as any,
            groupBy: 'activation' as any,
            aggregationColumns: [] as any[],
            sort: null as any,
            performance: 'skip-data' as any,
            skip: 0,
            limit: -1,
            zone,
          },
        }).subscribe(({ data }: any) => {
          const aggRows = data?.previewReport?.aggregationData?.rows ?? [];
          const aggHeaders = data?.previewReport?.aggregationData?.headers ?? [];
          if (aggRows.length > 0) {
            this.totals = {
              ...this.parseRow(aggHeaders, aggRows[0], 'Total'),
              overageRate: 0.25, saving: false,
            };
            this.totals.costPerActivation = this.totals.activationCount
              ? this.totals.totalCost / this.totals.activationCount
              : 0;
            this.totals.activationsPerJob = this.totals.jobCount
              ? this.totals.activationCount / this.totals.jobCount
              : 0;
            this.totals.costPerJob = this.totals.jobCount
              ? this.totals.totalCost / this.totals.jobCount
              : 0;
          }
        }),
      );
    });
  }

  private parseRows(headers: string[], rows: string[][]): BillingRow[] {
    return rows.map((row) => this.parseRow(headers, row, ''));
  }

  private parseRow(headers: string[], row: string[], _defaultName: string): BillingRow {
    const getNum = (col: string) => {
      const val = row[headers.indexOf(col)];
      return val ? parseFloat(val.replace(/[$,]/g, '')) : 0;
    };

    const parseZone = (colName: string): { name: string; id?: string } => {
      const raw = row[headers.indexOf(colName)];
      if (!raw || raw === 'None') return { name: 'Unassigned' };
      try {
        const obj = JSON.parse(raw);
        if (obj && typeof obj === 'object') {
          return { name: obj.name || 'Unknown', id: obj.id };
        }
        return { name: String(raw) };
      } catch {
        return { name: raw };
      }
    };

    const franchise = parseZone('Franchise');
    const franchiseId = row[headers.indexOf('Franchise ID')] || franchise.id;
    const overageCount = getNum('Overages');
    const overageCost = getNum('Overage Cost');
    const overageRate = overageCost && overageCount ? overageCost / overageCount : 0.25;

    return {
      zoneName: franchise.name,
      zoneId: franchiseId,
      franchiseId,
      franchiseName: franchise.name,
      activationCount: getNum('Activations'),
      promptTokens: getNum('Prompt Tokens'),
      completionTokens: getNum('Completion Tokens'),
      totalTokens: getNum('Total Tokens'),
      totalCost: getNum('Total Cost'),
      costPerActivation: getNum('Cost / Activation'),
      jobCount: getNum('Jobs'),
      activationsPerJob: getNum('Activations / Job'),
      costPerJob: getNum('Cost / Job'),
      askBeeCount: getNum('Ask Bee'),
      outboundCallCount: getNum('Outbound Call'),
      inboundCallCount: getNum('Inbound Call'),
      scheduleCommCount: getNum('Schedule Communication'),
      updateInventoryCount: getNum('Update Inventory'),
      updateInventoryCost: getNum('Inventory Cost'),
      updateInventoryTokens: getNum('Inventory Tokens'),
      monthlyRate: getNum('Monthly Rate'),
      billingCurrency: row[headers.indexOf('Currency')] || 'USD',
      includedActivations: getNum('Included'),
      overageCount,
      overageCost,
      totalCostBilling: getNum('Billing Total') || (getNum('Monthly Rate') + overageCost),
      overageRate,
      saving: false,
    };
  }
}